'use client';
import React from 'react';
import FirebaseAuth from 'Common/components/FirebaseAuth';
import { EmailAuthProvider, getAuth, GoogleAuthProvider } from 'firebase/auth';
import { getCookie, MimoblCookie } from '@bookpeazy/common-js';
import { AppCookie as Cookies, setCookie } from 'Common/functions/Cookie';
import Bugsnag from '@bugsnag/js';
import TOSCheckbox from 'Common/components/TOSCheckbox';
import NoSSR from 'Common/hoc/NoSSR';
import 'Clients/FireBaseClient';
import { AccountType } from 'Common/functions/AccountType';

type FirebaseAuthStageProps = {
  tosChecked: boolean;
  setTosChecked: (checked: boolean) => void;
  copyPrefix?: string;
  onSignedIn: () => void;
  accountType: AccountType;
  setFieldValue: (name: string, val: string) => void;
}

const FirebaseAuthStage: React.FC<FirebaseAuthStageProps> = ({
  tosChecked,
  setTosChecked,
  copyPrefix,
  onSignedIn,
  accountType,
  setFieldValue,
}) => {
  const handleSignInSuccess = async (user: any) => {
    try {
      const idToken = await user.getIdToken();
      const referrer = getCookie(MimoblCookie.USER_REFERRER);

      setFieldValue('idToken', idToken);
      setFieldValue('referrer', referrer || '');

      setCookie(Cookies.PeazyIsLoggedIn, 'true', 3600 * 24 * 14);

      onSignedIn();
    } catch (error) {
      console.error('Error handling sign-in success:', error);
      Bugsnag.notify('Error handling Firebase sign-in success: ' + error);
    }
  };

  const getTosLink = () => {
    return accountType === 1
      ? 'https://bookpeazy.com/terms-of-service-service-provider'
      : 'https://bookpeazy.com/peazy-terms-of-service';
  };

  return (
    <div className="my-6">
      <TOSCheckbox
        checked={tosChecked}
        onChange={() => setTosChecked(!tosChecked)}
        tosLink={getTosLink()}
      />
      <div className={tosChecked ? '' : 'pointer-events-none'}>
        <NoSSR>
          <FirebaseAuth
            className="firebase-auth"
            copyPrefix={copyPrefix}
            uiConfig={{
              signInFlow: 'popup',
              signInOptions: [
                EmailAuthProvider.PROVIDER_ID,
                {
                  provider: GoogleAuthProvider.PROVIDER_ID,
                  customParameters: { prompt: 'select_account' }
                }
              ],
              siteName: 'Peazy',
              callbacks: {
                signInSuccessWithAuthResult: ({ user }) => {
                  handleSignInSuccess(user);
                  return false; // Prevent default navigation
                },
                signInFailure: (error) => {
                  Bugsnag.notify('Firebase signin error: ' + error.toJSON());
                },
              }
            }}
            firebaseAuth={getAuth()}
          />
        </NoSSR>
      </div>
    </div>
  );
};

export default FirebaseAuthStage;
